import React, { useEffect, useMemo, useState } from 'react';
import { Box, Breadcrumbs, Link } from '@mui/material';
import Grid from '@mui/system/Unstable_Grid';
import moment from 'moment';
import * as XLSX from 'xlsx';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { PaginationFilterType } from '@types';
import { DynamicCard, Table } from '@components';
const money = (value) => (value ?? 0).toLocaleString('pt-PT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 });
const EMPTY = { processos: [], total: 0, count: 0 };
const GasFaturacaoPosteriorView = ({ $state, GasService, UIService }) => {
    const [from, setFrom] = useState(() => ($state.params.from ? moment($state.params.from) : moment().startOf('month')));
    const [to, setTo] = useState(() => ($state.params.to ? moment($state.params.to) : moment()));
    const [loading, setLoading] = useState(false);
    const [raw, setRaw] = useState(EMPTY);
    const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 20 });
    const [sort, setSort] = useState([]);
    const [filter, setFilter] = useState([]);
    const fetchData = async () => {
        if (!from || !to || !from.isValid() || !to.isValid())
            return;
        setLoading(true);
        try {
            const result = await GasService.getFaturacaoPosterior(from.format('YYYY-MM-DD'), to.format('YYYY-MM-DD'));
            setRaw(result ?? EMPTY);
            $state.go($state.current.name, { from: from.format('YYYY-MM-DD'), to: to.format('YYYY-MM-DD') }, { notify: false, reload: false, location: 'replace', inherit: true });
            console.log(result);
        }
        catch (e) {
            console.log(e);
            UIService.addToast('Ocorreu um erro ao carregar a faturação posterior.');
        }
        setLoading(false);
    };
    useEffect(() => {
        setPagination(p => ({ ...p, pageIndex: 0 }));
        fetchData();
    }, [from, to]);
    const columns = useMemo(() => {
        const moneyCell = ({ cell }) => <span>{money(cell.getValue() ?? 0)}</span>;
        const dateCell = ({ cell }) => <span>{cell.getValue() ? moment(cell.getValue()).format('DD/MM/YYYY') : '—'}</span>;
        const alignRight = { muiTableHeadCellProps: { align: 'right' }, muiTableBodyCellProps: { align: 'right' } };
        return [
            { accessorKey: 'numeroProcesso', header: 'Nº Processo', size: 110 },
            { accessorKey: 'numeroParceiro', header: 'Nº Parceiro', size: 110 },
            { accessorKey: 'tecnicoNome', header: 'Nome do Técnico', size: 110 },
            { accessorKey: 'data', header: 'Data Inspeção', size: 110, Cell: dateCell },
            { accessorKey: 'duracao', header: 'Duração', size: 110 },
            { accessorKey: 'distrito', header: 'Distrito', size: 110 },
            { accessorKey: 'tipoInstalacao', header: 'Tipo Instalação', size: 130 },
            { accessorKey: 'estado', header: 'Estado', size: 130 },
            { accessorKey: 'totalProcesso', header: 'Total do Processo', Cell: moneyCell, ...alignRight }
        ];
    }, []);
    const options = useMemo(() => ({
        filter: [
            { name: 'numeroProcesso', type: PaginationFilterType.TEXT },
            { name: 'numeroParceiro', type: PaginationFilterType.TEXT },
            { name: 'duracao', type: PaginationFilterType.TEXT },
            { name: 'tecnicoNome', type: PaginationFilterType.TEXT },
            { name: 'distrito', type: PaginationFilterType.TEXT },
            { name: 'tipoInstalacao', type: PaginationFilterType.TEXT },
            { name: 'estado', type: PaginationFilterType.TEXT },
            { name: 'totalProcesso', type: PaginationFilterType.NUMBER }
        ],
        sort: ['numeroProcesso', 'numeroParceiro', 'tecnicoNome', 'data', 'duracao', 'distrito', 'tipoInstalacao', 'estado', 'totalProcesso']
    }), []);
    // Aplica filtro + ordenação + paginação em memória
    const data = useMemo(() => {
        let rows = [...raw.processos];
        filter.forEach(f => {
            const val = String(f.value ?? '').toLowerCase().trim();
            if (!val)
                return;
            rows = rows.filter(r => {
                const cell = r[f.id];
                if (cell == null)
                    return false;
                return String(cell).toLowerCase().includes(val);
            });
        });
        if (sort.length) {
            const s = sort[0];
            rows = [...rows].sort((a, b) => {
                const av = a[s.id];
                const bv = b[s.id];
                let c;
                if (typeof av === 'number' && typeof bv === 'number')
                    c = av - bv;
                else
                    c = String(av ?? '').localeCompare(String(bv ?? ''));
                return s.desc ? -c : c;
            });
        }
        const totalItems = rows.length;
        const pageSize = pagination.pageSize || 20;
        const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
        const start = pagination.pageIndex * pageSize;
        const pageRows = rows.slice(start, start + pageSize);
        return {
            data: pageRows,
            meta: { currentPage: pagination.pageIndex + 1, itemsPerPage: pageSize, totalItems, totalPages },
            links: {}
        };
    }, [raw, filter, sort, pagination]);
    const exportXlsx = () => {
        if (!raw.processos.length) {
            UIService.addToast('Sem dados para exportar.');
            return;
        }
        const header = ['Nº Processo', 'Nº Parceiro', 'Nome do Técnico', 'Data Inspeção', 'Duração', 'Distrito', 'Tipo Instalação', 'Estado', 'Tipo Fatura', 'Cobrado no local', 'Total do Processo'];
        const body = raw.processos.map(p => [
            p.numeroProcesso,
            p.numeroParceiro,
            p.tecnicoNome,
            p.data ? moment(p.data).format('DD/MM/YYYY') : '',
            p.duracao ?? '',
            p.distrito ?? '',
            p.tipoInstalacao ?? '',
            p.estado ?? '',
            p.totalProcesso
        ]);
        const totalRow = ['', '', '', '', '', '', '', 'TOTAL', '', raw.total];
        const ws = XLSX.utils.aoa_to_sheet([header, ...body, totalRow]);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Faturação Posterior');
        XLSX.writeFile(wb, `gas-faturacao-posterior-${from.format('YYYY-MM-DD')}-${to.format('YYYY-MM-DD')}.xlsx`);
    };
    const resumoContent = useMemo(() => [
        { label: 'Total a faturar:', value: money(raw.total) },
        { label: 'Nº de processos:', value: String(raw.count) }
    ], [raw]);
    return (<Box>
      <Box sx={{ mt: 2, mb: 2 }}>
        <Breadcrumbs aria-label='breadcrumb'>
          <Link underline='none' color='inherit'>
            Gás
          </Link>
          <Link underline='hover' color='text.primary'>
            Faturação Posterior
          </Link>
        </Breadcrumbs>
      </Box>

      <Grid container spacing={1}>
        <Grid xs={12} md={6}>
          <DynamicCard title='Filtros' sx={{ height: '100%' }} content={[
            {
                label: 'De:',
                value: (<LocalizationProvider dateAdapter={AdapterMoment}>
                    <DatePicker format='DD/MM/YYYY' value={from} onChange={value => value && setFrom(value)} slotProps={{ textField: { variant: 'outlined', fullWidth: true, size: 'small' } }}/>
                  </LocalizationProvider>)
            },
            {
                label: 'Até:',
                value: (<LocalizationProvider dateAdapter={AdapterMoment}>
                    <DatePicker format='DD/MM/YYYY' value={to} onChange={value => value && setTo(value)} slotProps={{ textField: { variant: 'outlined', fullWidth: true, size: 'small' } }}/>
                  </LocalizationProvider>)
            }
        ]} loading={false}/>
        </Grid>
        <Grid xs={12} md={6}>
          <DynamicCard title='Resumo' sx={{ height: '100%' }} content={resumoContent} loading={false}/>
        </Grid>

        <Grid xs={12} md={12}>
          <Table storageKey={$state.current.name} data={data} columns={columns} initial={{ sort, filter }} isLoading={loading} options={options} onPaginate={setPagination} onFilter={setFilter} onSort={setSort} onExport={exportXlsx}/>
        </Grid>
      </Grid>
    </Box>);
};
export default GasFaturacaoPosteriorView;
